import { useRef, useState } from 'react';
import type { AutonomyLevel, SessionInfo } from '@cm/protocol';
import { AUTONOMY_LABELS, AUTONOMY_RISK } from '@cm/protocol';
import { api, ApiError } from '../api';
import { MAX_PANE_NAME, paneColor, paneFontSize, paneName, setPanePrefs } from '../layout';
import { useActiveLayout, useStore } from '../store';
import { agentColor } from '../theme';
import { AgentMark } from './AgentMark';
import { PaneColorPopover } from './PaneColorPopover';
import { TerminalPane } from './TerminalPane';

interface Props {
  session: SessionInfo;
  focused: boolean;
  /** Avisa a grade que este pane comecou a ser arrastado para reposicionar. */
  onDragStart?: () => void;
  /** No celular o pane ocupa a tela e nao muda de lugar. */
  reorderable?: boolean;
}

/** Rótulos curtos: a pílula é estreita e o texto longo estouraria o cabeçalho. */
const AUTONOMY_SHORT: Record<AutonomyLevel, string> = {
  manual: 'manual',
  plan: 'plan',
  'auto-edit': 'auto-edit',
  'full-auto': 'full-auto',
  yolo: 'yolo',
};

export function SessionPane({ session, focused, onDragStart, reorderable = true }: Props) {
  const focus = useStore((s) => s.focus);
  const pushToast = useStore((s) => s.pushToast);
  const removeSession = useStore((s) => s.removeSession);
  const agents = useStore((s) => s.agents);
  const updateLayout = useStore((s) => s.updateLayout);
  const tokens = useStore((s) => s.theme.tokens);
  const layout = useActiveLayout();
  const [busy, setBusy] = useState(false);

  // Renomear no lugar: o selo do titulo vira input no duplo clique.
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [picking, setPicking] = useState(false);
  const colorBtnRef = useRef<HTMLButtonElement | null>(null);

  const capability = agents.find((a) => a.agent === session.agent);
  const levels = capability?.supportedAutonomy ?? [];
  const canCycle = capability?.supportsRuntimeAutonomyToggle ?? false;

  const name = paneName(layout, session.id, session.title);
  const ownColor = paneColor(layout, session.id);
  const ownFont = paneFontSize(layout, session.id);
  // A cor escolhida pelo usuario, ou a identidade do agente. E o valor que
  // alimenta --agent-color, ja consumido por sete regras do styles.css -- a
  // faixa superior, a borda de foco, o selo, o item da sidebar e o chip
  // recolhido passam a obedecer sem uma linha de CSS nova.
  const accent = ownColor ?? agentColor(tokens, session.agent);

  function patchPrefs(patch: { name?: string; color?: string; fontSize?: number }) {
    if (!session.workspaceId) return;
    updateLayout(session.workspaceId, (l) => setPanePrefs(l, session.id, patch));
  }

  function commitRename() {
    setRenaming(false);
    const next = draft.trim();
    // Igual ao titulo do daemon, ou vazio: limpa o apelido em vez de guardar
    // uma duplicata que deixaria de acompanhar um rename futuro do daemon.
    patchPrefs({ name: next && next !== session.title ? next : '' });
  }

  async function changeAutonomy(next: AutonomyLevel) {
    if (next === session.autonomy) return;
    setBusy(true);
    try {
      // A primeira tentativa vai sem allowRestart de propósito: o daemon
      // responde 409 explicando que precisa respawnar, e aí confirmamos.
      const first = await api.setAutonomy(session.id, next, false);
      if (first.applied !== 'rejected') return;
      throw new ApiError(first.reason ?? 'recusado', 409);
    } catch (err) {
      const needsRestart =
        err instanceof ApiError && err.status === 409 && /reinici/i.test(err.message);
      if (!needsRestart) {
        pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
        setBusy(false);
        return;
      }
      const ok = window.confirm(
        `Mudar para "${AUTONOMY_LABELS[next]}" reinicia o processo do agente.\n\n` +
          'A conversa é retomada quando o agente suporta, e o pane continua aberto. Seguir?',
      );
      if (!ok) {
        setBusy(false);
        return;
      }
      try {
        const result = await api.setAutonomy(session.id, next, true);
        if (result.applied === 'rejected') {
          pushToast({ kind: 'error', text: result.reason ?? 'não foi possível mudar o nível' });
        } else {
          pushToast({ kind: 'success', text: `agora em ${AUTONOMY_LABELS[next]}` });
        }
      } catch (retryErr) {
        pushToast({
          kind: 'error',
          text: retryErr instanceof Error ? retryErr.message : String(retryErr),
        });
      }
    } finally {
      setBusy(false);
    }
  }

  async function act(
    label: string,
    fn: () => Promise<unknown>,
    after?: () => void,
  ): Promise<void> {
    setBusy(true);
    try {
      await fn();
      pushToast({ kind: 'info', text: label });
      after?.();
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  const running = session.status === 'running';
  const risk = AUTONOMY_RISK[session.autonomy];

  return (
    <div
      className={`pane${focused ? ' focused' : ''}${running ? '' : ' dead'}`}
      // --term-bg tinge o padding do .pane-term junto com o canvas do xterm.
      // Sem ele sobraria uma moldura de 7px na cor antiga em volta do terminal
      // tingido, porque o padding e do elemento, nao do canvas.
      style={{
        ['--agent-color' as string]: accent,
        ['--term-bg' as string]: `color-mix(in srgb, var(--bg-deep) 94%, ${accent})`,
      }}
      onMouseDown={() => focus(session.id)}
    >
      {/* O cabecalho inteiro e a alca de arraste, como numa janela de verdade.
          O terminal fica fora dela para nao roubar a selecao de texto. */}
      <div
        className="pane-head"
        // O cabecalho inteiro e a alca de arraste, mas um <input> dentro de um
        // ancestral draggable nao recebe caret nem selecao por arraste: o
        // mousedown vira gesto de drag antes de chegar no campo. Tirar o
        // atributo enquanto o rename esta aberto e o unico jeito confiavel.
        draggable={reorderable && !renaming}
        onDragStart={(e) => {
          if (renaming) {
            e.preventDefault();
            return;
          }
          e.dataTransfer.effectAllowed = 'move';
          // Firefox exige algum payload para iniciar o arraste.
          e.dataTransfer.setData('text/plain', session.id);
          onDragStart?.();
        }}
      >
        <span className="drag-handle" title="Arraste para reposicionar">
          ⠿
        </span>
        <AgentMark agent={session.agent} size={15} />

        <span
          className="pane-badge"
          title={renaming ? undefined : `${name}\n\n${session.argv.join(' ')}`}
        >
          {renaming ? (
            <input
              className="pane-rename"
              value={draft}
              autoFocus
              maxLength={MAX_PANE_NAME}
              spellCheck={false}
              aria-label="Renomear pane"
              onFocus={(e) => e.currentTarget.select()}
              // Impede que o mousedown chegue ao .pane, que chamaria focus() e
              // devolveria o teclado ao xterm no meio da digitacao.
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                // O terminal escuta teclado no pane; sem isto um Enter aqui
                // tambem chegaria ao agente.
                e.stopPropagation();
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setRenaming(false);
              }}
            />
          ) : (
            <span
              className="label"
              tabIndex={0}
              role="button"
              onDoubleClick={() => {
                setDraft(name);
                setRenaming(true);
              }}
              // Duplo clique nao e descobrivel e nao alcancavel por teclado;
              // F2 e Enter no selo focado abrem o mesmo rename.
              onKeyDown={(e) => {
                if (e.key === 'F2' || e.key === 'Enter') {
                  e.preventDefault();
                  setDraft(name);
                  setRenaming(true);
                }
              }}
            >
              {name}
            </span>
          )}
        </span>

        <button
          ref={colorBtnRef}
          className={`ghost pane-color-btn${ownColor ? ' custom' : ''}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setPicking((v) => !v)}
          disabled={!session.workspaceId}
          title="Cor deste pane"
          aria-label="Cor deste pane"
        >
          ◨
        </button>

        {picking && (
          <PaneColorPopover
            value={ownColor}
            inherited={agentColor(tokens, session.agent)}
            anchor={colorBtnRef.current}
            fontSize={ownFont}
            onPick={(color) => patchPrefs({ color: color ?? '' })}
            onPickFont={(size) => patchPrefs({ fontSize: size ?? 0 })}
            onClose={() => setPicking(false)}
          />
        )}

        <span className="spacer" />

        <span className={`autonomy-pill risk-${risk}`}>{AUTONOMY_SHORT[session.autonomy]}</span>

        <select
          className="autonomy-select"
          value={session.autonomy}
          disabled={busy || !running}
          onChange={(e) => void changeAutonomy(e.target.value as AutonomyLevel)}
          title="Trocar o nível de autonomia (reinicia o agente)"
        >
          {levels.map((level) => (
            <option key={level} value={level}>
              {AUTONOMY_LABELS[level]}
            </option>
          ))}
        </select>

        {canCycle && (
          <button
            className="ghost"
            onClick={() => void act('atalho enviado ao agente', () => api.cycleAutonomy(session.id))}
            disabled={!running}
            title="Envia Shift+Tab para o agente ciclar o modo de permissão por conta própria"
          >
            ⇧⇥
          </button>
        )}

        <button
          className="ghost"
          onClick={() =>
            session.workspaceId &&
            updateLayout(session.workspaceId, (l) => ({
              ...l,
              minimized: [...l.minimized, session.id],
            }))
          }
          disabled={!session.workspaceId}
          title="Minimizar — o agente continua rodando"
        >
          ▁
        </button>

        <button
          className="ghost"
          onClick={() =>
            void act('sessão reiniciada', () => api.restartSession(session.id, { resume: true }))
          }
          disabled={busy}
          title="Reiniciar o agente, retomando a conversa"
        >
          ↻
        </button>

        <button
          className="ghost danger"
          onClick={() => {
            if (!window.confirm(`Encerrar "${name}"?`)) return;
            void act('sessão encerrada', () => api.killSession(session.id), () =>
              window.setTimeout(() => removeSession(session.id), 6_000),
            );
          }}
          disabled={busy}
          title="Encerrar"
        >
          ✕
        </button>
      </div>

      <TerminalPane
        session={session}
        focused={focused}
        accent={accent}
        fontOverride={ownFont}
        onFocus={() => focus(session.id)}
      />

      {session.status === 'exited' && session.errorMessage && (
        <div className="pane-error" title={session.errorMessage}>
          <strong>o agente encerrou:</strong> {session.errorMessage}
        </div>
      )}

      <div className="pane-foot">
        <span className={running ? 'live' : session.status === 'starting' ? 'booting' : 'stopped'}>
          {running ? `● pid ${session.pid}` : session.status === 'starting' ? '◌ subindo' : `○ saiu ${session.exitCode ?? ''}`}
        </span>
        {session.model && <span>{session.model}</span>}
        <span className="cwd" title={session.cwd}>
          {session.cwd}
        </span>
        <span>
          {session.cols}×{session.rows}
        </span>
      </div>
    </div>
  );
}
